import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { default as ExportButton } from 'components/Button'
import FilterDrawer from 'components/FilterDrawer/FilterDrawer'
import { Filter } from 'components/FilterDrawer/FilterDrawer.config'
import Icon from 'components/Icon'
import Loader from 'components/Loader/Loader'
import Tooltip from 'components/Tooltip'
import { useTableInfiniteScroll } from 'containers/VerticalPage/common/useTableInfiniteScroll'
import { useCountDocuments } from 'hooks/useCountDocuments'
import { useDebounce } from 'hooks/useDebounce'
import { useFilterOptionsQuery } from 'hooks/useFilterOptionsQuery'
import useVerticalSearchAndSort from 'hooks/useVerticalSearchAndSort'
import { MaterialReactTable, MRT_RowVirtualizer, useMaterialReactTable } from 'material-react-table'
import React, { useEffect, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'
import { onEmployeesCSVExport } from 'utils/employees'
import { filtersInitialState } from './EmployeesTab.config'
import styles from './EmployeesTab.module.scss'
import SearchBar from './SearchBar'
import useEmployeesColumns from './useEmployeesColumns'
import { useEmployeesQuery } from './useEmployeesQuery'
import useEmployeesTableOptions from './useEmployeesTableOptions'

interface EmployeesTabProps {
  caseId: string
  verticalEntity?: any
  activityIndices: string
}

const EmployeesTab = ({ caseId, verticalEntity, activityIndices }: EmployeesTabProps) => {
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const match = useRouteMatch<{ vertical: string; caseId: string }>('/vertical/:vertical/:caseId')
  const vertical = match?.params.vertical || ''

  const { searchValue, searchValueDebounced, setSearchValue } = useVerticalSearchAndSort()

  const [totalDBRowCount, setTotalDBRowCount] = useState(0)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)
  const [activeFilters, setActiveFilters] = useState<Filter[]>([])

  const activeFiltersDebounced = useDebounce(activeFilters, 500)

  const { data: docsCountData } = useCountDocuments({
    verticalIndex: activityIndices,
    currentQueryKey: 'employees-activities-key',
  })

  const { verticalDocumentActivity, isFetching, fetchNextPage, totalFetched } = useEmployeesQuery({
    caseId,
    zoneActivityIndices: activityIndices,
    pageSize: 10,
    searchValue: searchValueDebounced,
    filters: activeFiltersDebounced,
    sort: [],
  })

  const { filterOptions } = useFilterOptionsQuery({
    verticalIndices: activityIndices,
    currentQueryKey: 'employees-filter-options-key',
    filtersInitialState,
  })

  const { handleScroll } = useTableInfiniteScroll({
    pageSize: 10,
    totalFetched,
    totalDBRowCount,
    isFetching,
    tableContainerRef,
    fetchNextPage,
  })

  useEffect(() => {
    setTotalDBRowCount(docsCountData?.totalCount || 0)
  }, [docsCountData])

  const handleFilterButtonClick = () => {
    setIsFilterDrawerOpen(!isFilterDrawerOpen)
  }

  const handleExport = () => {
    onEmployeesCSVExport(
      activityIndices,
      caseId,
      vertical,
      verticalEntity?.entityDetails?.tin,
      verticalEntity?.entityDetails?.name,
    )
  }

  const columns = useEmployeesColumns()
  const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null)

  const tableOptions = useEmployeesTableOptions(
    columns,
    verticalDocumentActivity,
    isFetching,
    handleScroll,
    rowVirtualizerInstanceRef,
  )

  const table = useMaterialReactTable(tableOptions)

  return (
    <Stack direction='column' sx={{ position: 'relative', height: '100%' }} className={styles.mainStack}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <h2>Employees List</h2>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title='Export Entity Employees CSV'>
            <ExportButton color='light' outline className={styles.share} onClick={handleExport}>
              <Icon name='share' size='regular' />
            </ExportButton>
          </Tooltip>
          <SearchBar searchValue={searchValue} setSearchValue={setSearchValue} />
          <Button className={styles.actionsBarButton} onClick={handleFilterButtonClick}>
            <Icon size='standard' name='filterBlue' />
          </Button>
        </Box>
      </Box>
      {filterOptions && (
        <FilterDrawer
          open={isFilterDrawerOpen}
          onClose={() => setIsFilterDrawerOpen(false)}
          activeFilters={activeFilters}
          setActiveFilters={setActiveFilters}
          filtersInitialState={filtersInitialState}
          filterOptions={filterOptions}
        />
      )}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <MaterialReactTable table={table} />
      </Box>
      {isFetching && (
        <Box sx={{ position: 'absolute', bottom: '20px', left: '50%' }}>
          <Loader type='scrolling' />
        </Box>
      )}
    </Stack>
  )
}

export default EmployeesTab
